import styled from "@emotion/styled";
import React, { useRef, useState } from "react";
import useOnClickOutside from "@src/hooks/useOnClickOutside";
import Text from "@components/Text";

export type TOption = {
  title: string;
  value: string;
};

interface IProps {
  label?: string;
  options: TOption[];
  value?: string;
  onChange?: (value: string) => void;
  renderValue?: (option?: TOption) => React.ReactNode;
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
`;

const Tooltip = styled.div<{ opened: boolean }>`
  position: absolute;
  left: 0;
  top: 60px;
  background: #ffffff;
  height: ${({ opened }) => (opened ? "auto" : 0)};
  overflow: ${({ opened }) => (opened ? "auto" : "hidden")};
  border: ${({ opened }) => (opened ? 1 : 0)}px solid #cbd5d8;
  padding: ${({ opened }) => (opened ? "8px 0" : "0")};
  box-sizing: border-box;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.16);
  border-radius: 6px;
  max-height: 300px;
  overflow: auto;
  transition: 0.4s;
  z-index: 2000;
  min-width: 100%;
  width: max-content;
`;

const SelectItem = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  height: 38px;
  padding: 0 16px;
  background: ${({ selected }) => (selected ? "#f8f8f8" : "#fff")};
  transition: 0.4s;
  cursor: pointer;
  :hover {
    background: #f8f8f8;
  }
`;

const InputField = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  outline: none;
  padding: 0 12px 0 16px;
  height: 44px;
  background: #f8f8f8;
  border-radius: 4px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #494c5b;
  box-sizing: border-box;
  border: 1px solid transparent;
  transition: 0.4s;
  :hover,
  :focus {
    border: 1px solid #899093;
  }
  ::placeholder {
    font-family: Roboto, sans-serif;
    font-size: 14px;
    line-height: 20px;
    color: #cbd5d8;
  }
`;

const Select: React.FC<IProps> = ({ value, label, options, onChange, renderValue }) => {
  const [opened, setOpened] = useState(false);
  const ref = useRef(null);
  useOnClickOutside(ref, () => setOpened(false));

  const handleSelect = (val: string) => {
    onChange && onChange(val);
    setOpened(false);
  };

  const render = () => {
    const option = options.find((o) => o.value === value);
    if (renderValue) {
      return renderValue(option);
    }
    return option?.title;
  }

  return (
    <Root>
      <Text size={14} type="heading" color="#494C5B">{label}</Text>
      <InputField ref={ref} onClick={() => setOpened(true)}>
        {render()}
        <Tooltip opened={opened}>
          {options.map((option, i) => (
            <SelectItem
              key={i}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelect(option.value);
              }}
              selected={option.value === value}
            >
              {option.title}
            </SelectItem>
          ))}
        </Tooltip>
      </InputField>
    </Root>
  );
};
export default Select;
